import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import type { FC } from "react";
import { ICON_SIZE } from "@/theme/tokens";
import MetaChip from "../Badges/MetaChip";
import { TREND_DIRECTION_ICON, TREND_SENTIMENT_CHIP_TONE, type TrendDirection, type TrendSentiment } from "../trend";

export type TrendChipProps = {
	/** Already-translated, already-signed text ("+38.0 h"). */
	label: string;
	direction: TrendDirection;
	sentiment: TrendSentiment;
	/** Already-translated text naming the baseline ("vs the previous 4 weeks"); also read to screen readers. */
	// DEFAULT: absent = no tooltip.
	tooltip?: string;
	dataTestId?: string;
	/** Inherit the reading direction when the label's signed figures already carry bidi isolates. */
	textDirection?: "ltr" | "rtl" | "inherit";
};

/**
 * A change as a chip, with an LTR label unless the caller supplies its own bidi-isolated figures.
 */
// DEFAULT: existing callers keep the whole label left-to-right, preserving signed-number rendering.
export const TrendChip: FC<TrendChipProps> = ({ label, direction, sentiment, tooltip, dataTestId, textDirection = "ltr" }) => {
	const DirectionIcon = TREND_DIRECTION_ICON[direction];
	return (
		<>
			<MetaChip
				label={label}
				icon={<DirectionIcon sx={{ fontSize: ICON_SIZE.PILL }} />}
				tone={TREND_SENTIMENT_CHIP_TONE[sentiment]}
				tooltip={tooltip}
				sx={{ fontVariantNumeric: "tabular-nums", direction: textDirection, unicodeBidi: "isolate" }}
				dataTestId={dataTestId}
			/>
			{/* A read-only chip is no tab stop, so its hover text never reaches a screen reader: "+12.5 h" vs WHAT. */}
			{tooltip !== undefined && (
				<Box component="span" sx={visuallyHidden}>
					{tooltip}
				</Box>
			)}
		</>
	);
};
